/**
 * Shared XEP-0050 / XEP-0004 plumbing for the agent's automation commands.
 *
 * Mirrors the agent's own `src/cron/adhocStanza.ts`: one submit form per
 * command, snake_case field names, and a result form the caller reads by name.
 */
import { Client, xml } from "@xmpp/client"
import { Element } from "@xmpp/xml"
import iqMeasure from "@/instrumentation/iqMeasure"

export const ADHOC_XMLNS = "http://jabber.org/protocol/commands"
export const DATAFORM_XMLNS = "jabber:x:data"

/**
 * The agent's ad-hoc surface listens on its full JID; the bare
 * `transchat@<vhost>` address is the pubsub service, which is not the same
 * thing and would answer service-unavailable here.
 */
export const agentJid = (xmpp: Client): string => {
  // Mid-reconnect the client exists with `jid` still unset — the callers treat
  // that as "no client", so say so plainly instead of asserting it away.
  if (!xmpp.jid)
    throw new Error("Cannot address the agent: the XMPP client has no jid yet")

  return `transchat@${xmpp.jid.domain}/transchat`
}

export type AdhocField = Element | null

/**
 * Optional fields are dropped rather than sent empty — the agent treats a
 * present-but-empty field as an explicit value, and for `message` that would
 * blank the instruction instead of leaving it alone.
 */
export const formField = (
  name: string,
  value: string | undefined,
): AdhocField =>
  value === undefined
    ? null
    : xml("field", { var: name }, xml("value", {}, value))

export const sendCommand = (
  xmpp: Client,
  node: string,
  fields: AdhocField[],
): Promise<Element> =>
  iqMeasure(xmpp.iqCaller).request(
    xml(
      "iq",
      { type: "set", to: agentJid(xmpp) },
      xml(
        "command",
        { node, action: "execute", xmlns: ADHOC_XMLNS },
        xml(
          "x",
          { xmlns: DATAFORM_XMLNS, type: "submit" },
          ...fields.filter((f): f is Element => f !== null),
        ),
      ),
    ),
  )

export const readResultField = (
  reply: Element,
  name: string,
): string | undefined =>
  reply
    .getChild("command")
    ?.getChild("x", DATAFORM_XMLNS)
    ?.getChildren("field")
    .find((f: Element) => f.attrs.var === name)
    ?.getChildText("value") ?? undefined

/**
 * The agent maps every domain rejection onto a stanza error, so @xmpp/client
 * rejects the request. `item-not-found` is the one callers branch on: for a
 * per-stop rule it means "it already fired and deleted itself", for a cron it
 * means "no such job, or not yours".
 */
export const isNotFoundError = (err: unknown): boolean =>
  typeof err === "object" &&
  err !== null &&
  (err as { condition?: string }).condition === "item-not-found"
